/**
 * Deep-link resolution for Task WorkItems.
 *
 * Maps a task type + entity coordinates + routing tags into the most
 * specific Galileo sub-page URL for that task. Returns null when no
 * meaningful deep link can be constructed (unknown task type, or the
 * entity type doesn't correspond to a Galileo page), signaling to the
 * UI that the "Go" button should not render.
 *
 * The mapping gracefully degrades: when a task type targets a
 * carrier-specific sub-page but no carrierId is available in routing
 * tags, it falls back to the entity root page.
 */

import { galileoEntityHref } from "./galileoLinks";

/**
 * Build a Galileo cover-desk path for a load+carrier combination.
 * If carrierId is absent, returns the plain load detail path.
 */
function loadCarrierPath(loadId: string, carrierId: string | undefined): string {
    const encodedLoad = encodeURIComponent(loadId);
    if (carrierId !== undefined && carrierId.length > 0) {
        return `/loads/${encodedLoad}/carriers/${encodeURIComponent(carrierId)}`;
    }
    return `/loads/${encodedLoad}`;
}

interface DeepLinkContext {
    readonly taskType: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly routingTags: Readonly<Record<string, string>>;
}

/**
 * Resolve the Galileo deep-link path for a task work item.
 *
 * Returns null when:
 * - The task type is unknown (not in the mapping table)
 * - The entity type has no corresponding Galileo page
 * - The entity id is empty
 */
export function galileoTaskDeepLink(ctx: DeepLinkContext): string | null {
    const { taskType, entityType, entityId, routingTags } = ctx;

    if (entityId.length === 0) return null;

    const normalizedEntityType = entityType.toLowerCase();
    const carrierId = routingTags["carrierId"] ?? routingTags["CarrierId"] ?? undefined;

    switch (taskType) {
        // Call tasks — navigate to the load page. These tasks are
        // fundamentally about tracking the physical shipment, so
        // the load root (with its route itinerary) is the right target.
        case "pickup-arrival-call":
        case "pickup-departure-call":
        case "enroute-call":
            if (normalizedEntityType !== "load") {
                return galileoEntityHref(entityType, entityId);
            }
            return `/loads/${encodeURIComponent(entityId)}`;

        // Vetting review tasks — navigate directly to the load's Vetting
        // panel. Galileo owns the panel state in the query string.
        case "vetting-carrier-vin":
        case "vetting-driver-location":
            if (normalizedEntityType !== "load") {
                return galileoEntityHref(entityType, entityId);
            }
            return `/loads/${encodeURIComponent(entityId)}?panel=vetting`;

        // E3 manual review — the thing under review is one carrier's vetting
        // attempt, so the carrier sub-page is the only place the operator can
        // read it. Falls back to the load root panel when no carrier is tagged.
        case "vetting-e3-review":
            if (normalizedEntityType !== "load") {
                return galileoEntityHref(entityType, entityId);
            }
            return `${loadCarrierPath(entityId, carrierId)}?panel=vetting`;

        // Carrier-specific tasks — navigate to the load's carrier
        // sub-page when carrierId is available, otherwise the load root.
        case "send-rate-confirmation":
        case "driver-vetting-failed":
            if (normalizedEntityType !== "load") {
                return galileoEntityHref(entityType, entityId);
            }
            return loadCarrierPath(entityId, carrierId);

        default:
            // Unknown task type — fall back to entity root if possible.
            return galileoEntityHref(entityType, entityId);
    }
}

/**
 * Where the "Go" affordance should send a work item, or null when there is
 * nothing to open (so the caller renders no button).
 *
 * `external` carries a producer-supplied absolute URL verbatim; the dispatch
 * layer decides in-place relay vs. new tab by origin. `internal` carries an
 * origin-relative Galileo path for the peer relay.
 */
export type WorkItemActionTarget =
    | { readonly kind: "external"; readonly url: string }
    | { readonly kind: "internal"; readonly path: string };

interface WorkItemActionContext extends DeepLinkContext {
    /** Producer-supplied override; null/empty means fall back to deep link. */
    readonly externalActionUrl: string | null | undefined;
}

/**
 * Resolve the "Go" target for a work item under the fixed precedence:
 *   1. an explicit, producer-supplied `externalActionUrl` (highest),
 *   2. else the computed Galileo deep link,
 *   3. else null (no affordance).
 *
 * Pure and side-effect free so both the click handler and the button-
 * visibility predicate can share one source of truth — they must agree, or a
 * visible button would no-op (or a hidden one would have worked).
 */
export function resolveWorkItemActionTarget(
    ctx: WorkItemActionContext,
): WorkItemActionTarget | null {
    const external = ctx.externalActionUrl;
    if (external !== null && external !== undefined && external.length > 0) {
        return { kind: "external", url: external };
    }

    const path = galileoTaskDeepLink(ctx);
    return path !== null ? { kind: "internal", path } : null;
}
